import * as React from "react"
import { Box } from "@chakra-ui/react"
import {
  BIKE_SERVICE_KEY,
  BIKE_MAINTENANCE_KEY,
  BIKE_DROPZONE_KEY,
  TOPBAR_HEIGHT,
  NAV_WIDTH,
} from "../lib/config"
import { AlertEntity } from "../lib/types/alert"
import { Bbox, checkValidCoordinatesArray } from "../lib/helpers/map"
import {
  useGetMaintenanceZonesQuery,
  useGetServiceZonesQuery,
  useGetDropzonesQuery,
} from "../lib/hooks/queries/useGetMapZones"
import { useStoredDisclosure } from "../lib/hooks/useStoredDisclosure"
import { groupAlerts } from "../lib/helpers/alerts"
import { MapZoneControl } from "./MapZoneControl"
import AlertsMapMapbox from "./AlertsMapMapbox"
import AlertsMapGoogleMap from "./AlertsMapGoogleMap"
import { useDeepCompareMemo } from "use-deep-compare"
import { useMapSettings } from "./providers/MapSettingsProvider"
import { ErrorBoundary } from "./ErrorBoundary"

interface Props {
  alerts: AlertEntity[]
  bbox: Bbox
  onDeselectAlert: () => void
  refetchAlerts: () => Promise<any>
  selectedAlertUuid?: string
  isLoading: boolean
  onDeselectBike: () => void
  selectedBikeUuid?: string
  setSelectedBikeUuid: (uuid: string) => void
}

export const AlertsMap: React.FC<Props> = ({
  alerts,
  bbox,
  selectedAlertUuid,
  onDeselectAlert,
  refetchAlerts,
  isLoading,
  selectedBikeUuid,
  setSelectedBikeUuid,
  onDeselectBike,
}) => {
  const { mapProvider } = useMapSettings()

  const { isOpen: showServiceZones, onToggle: onServiceZoneToggle } = useStoredDisclosure({
    key: BIKE_SERVICE_KEY,
    defaultIsOpen: true,
  })
  const { isOpen: showDropzones, onToggle: onDropzoneToggle } = useStoredDisclosure({
    key: BIKE_DROPZONE_KEY,
    defaultIsOpen: true,
  })
  const { isOpen: showMaintenanceZones, onToggle: onMaintenanceToggle } = useStoredDisclosure({
    key: BIKE_MAINTENANCE_KEY,
    defaultIsOpen: true,
  })

  const { serviceZones } = useGetServiceZonesQuery({ enabled: showServiceZones })
  const { dropzones } = useGetDropzonesQuery({ enabled: showDropzones })
  const { maintenanceZones } = useGetMaintenanceZonesQuery({ enabled: showMaintenanceZones })

  // const groupedAlerts = useDeepCompareMemo(() => groupAlerts(alerts), [alerts])

  const groupedAlertsEntries = useDeepCompareMemo(() => {
    const groupedAlerts = groupAlerts(alerts)
    return Object.entries(groupedAlerts).filter(([_, { bicycle, zone }]) => {
      if (bicycle) {
        return checkValidCoordinatesArray(bicycle.bicycle_coordinates)
      }
      if (zone) {
        return !!zone.location && checkValidCoordinatesArray(zone.location.coordinates)
      }
      return false
    })
  }, [alerts])

  return (
    <Box
      position="relative"
      style={{
        height: `calc(100vh - ${TOPBAR_HEIGHT})`,
        width: `calc(100vw - ${NAV_WIDTH})`,
      }}
    >
      <ErrorBoundary>
        {mapProvider === "google_maps" ? (
          <AlertsMapGoogleMap
            bbox={bbox}
            selectedAlertUuid={selectedAlertUuid}
            onDeselectAlert={onDeselectAlert}
            refetchAlerts={refetchAlerts}
            isLoading={isLoading}
            selectedBikeUuid={selectedBikeUuid}
            setSelectedBikeUuid={setSelectedBikeUuid}
            onDeselectBike={onDeselectBike}
            serviceZones={serviceZones}
            dropzones={dropzones}
            maintenanceZones={maintenanceZones}
            showDropzones={showDropzones}
            showServiceZones={showServiceZones}
            showMaintenanceZones={showMaintenanceZones}
            groupedAlertsEntries={groupedAlertsEntries}
          />
        ) : (
          <AlertsMapMapbox
            bbox={bbox}
            selectedAlertUuid={selectedAlertUuid}
            onDeselectAlert={onDeselectAlert}
            refetchAlerts={refetchAlerts}
            isLoading={isLoading}
            selectedBikeUuid={selectedBikeUuid}
            setSelectedBikeUuid={setSelectedBikeUuid}
            onDeselectBike={onDeselectBike}
            serviceZones={serviceZones}
            dropzones={dropzones}
            maintenanceZones={maintenanceZones}
            showDropzones={showDropzones}
            showServiceZones={showServiceZones}
            showMaintenanceZones={showMaintenanceZones}
            groupedAlertsEntries={groupedAlertsEntries}
          />
        )}
      </ErrorBoundary>
      <MapZoneControl
        showDropzones={showDropzones}
        showServiceZones={showServiceZones}
        showMaintenanceZones={showMaintenanceZones}
        onDropzoneToggle={onDropzoneToggle}
        onServiceZoneToggle={onServiceZoneToggle}
        onMaintenanceToggle={onMaintenanceToggle}
      />
    </Box>
  )
}
